/** V2 — Écrans EMPRUNTEURS + revenus du foyer. */
import { SITUATIONS, ANCIENNETE_SECTEUR, ANCIENNETE_ACTIVITE, AUTRE_DETAILS, REVENU_LABEL } from "./simConstants";
import { BigButton, ChoiceCard, Chips, YesNo, StepTitle, SubLabel, AmountInput } from "./SimPrimitives";

export function StepFoyer({ draft, patch, next }) {
  return (
    <div>
      <StepTitle emoji="👥" title="Qui emprunte ?" />
      <div className="grid grid-cols-2 gap-3">
        {[{ v: 1, label: "Seul", emoji: "👤" }, { v: 2, label: "À deux", emoji: "👥" }].map((o) => (
          <ChoiceCard key={o.v} selected={draft.nb_emprunteurs === o.v} emoji={o.emoji} label={o.label} onClick={() => patch({ nb_emprunteurs: o.v })} testid={`sim-foyer-emp-${o.v}`} />
        ))}
      </div>
      <SubLabel>Enfants à charge</SubLabel>
      <div className="grid grid-cols-5 gap-2">
        {[0, 1, 2, 3, 4].map((n) => (
          <button key={n} type="button" onClick={() => patch({ nb_enfants: n })} data-testid={`sim-foyer-enfants-${n}`} aria-pressed={draft.nb_enfants === n}
            className={`min-h-[48px] rounded-2xl border-2 font-extrabold text-lg sim-chip ${draft.nb_enfants === n ? "border-[color:var(--cf-primary)] bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary-text)]" : "border-slate-200 bg-white text-slate-700"}`}>
            {n === 4 ? "4+" : n}
          </button>
        ))}
      </div>
      <div className="mt-8"><BigButton onClick={next} disabled={draft.nb_emprunteurs == null} testid="sim-foyer-next">Suivant →</BigButton></div>
    </div>
  );
}

function SubQuestions({ e, set, testid }) {
  const s = e.situation_pro;
  if (s === "salarie_prive") return (<><SubLabel>Période d'essai en cours ?</SubLabel><YesNo value={e.periode_essai} onChange={(v) => set({ periode_essai: v })} testid={`${testid}-essai`} /></>);
  if (s === "fonctionnaire") return (<><SubLabel>Êtes-vous titulaire ?</SubLabel><YesNo value={e.titulaire} onChange={(v) => set({ titulaire: v })} testid={`${testid}-titulaire`} /></>);
  if (s === "cdd" || s === "interim") return (<><SubLabel>Depuis combien de temps dans ce secteur ?</SubLabel><Chips options={ANCIENNETE_SECTEUR} value={e.anciennete} onChange={(k) => set({ anciennete: k })} testid={`${testid}-anciennete`} /></>);
  if (s === "independant" || s === "chef_entreprise") return (<><SubLabel>Ancienneté de votre activité</SubLabel><Chips options={ANCIENNETE_ACTIVITE} value={e.anciennete} onChange={(k) => set({ anciennete: k })} testid={`${testid}-anciennete`} /></>);
  if (s === "autre") return (<><SubLabel>Précisez</SubLabel><Chips options={AUTRE_DETAILS} value={e.autre_detail} onChange={(k) => set({ autre_detail: k })} testid={`${testid}-detail`} cols={2} /></>);
  return null;
}

function subOk(e) {
  const s = e.situation_pro;
  if (s === "salarie_prive") return e.periode_essai != null;
  if (s === "fonctionnaire") return e.titulaire != null;
  if (s === "cdd" || s === "interim" || s === "independant" || s === "chef_entreprise") return !!e.anciennete;
  if (s === "autre") return !!e.autre_detail;
  return !!s;
}

export function StepProfil({ draft, patchNested, next, idx }) {
  const key = `emprunteur_${idx}`;
  const e = draft[key];
  const set = (p) => patchNested(key, p);
  const ageNum = Number(e.age);
  const ageOk = e.age !== "" && ageNum >= 18 && ageNum <= 99;
  const ok = ageOk && subOk(e);
  return (
    <div>
      <StepTitle emoji={idx === 1 ? "🧑" : "🧑‍🤝‍🧑"} title={idx === 1 ? (draft.nb_emprunteurs === 2 ? "Premier emprunteur" : "Parlons de vous") : "Second emprunteur"} />
      <SubLabel>{idx === 1 ? "Quel âge avez-vous ?" : "Quel âge a-t-il / a-t-elle ?"}</SubLabel>
      <AmountInput value={e.age} onChange={(x) => set({ age: x.slice(0, 2) })} suffix="ans" placeholder="34" testid={`sim-e${idx}-age-input`} autoFocus />
      {e.age !== "" && !ageOk && <p className="text-[11.5px] text-red-600 mt-1.5" data-testid={`sim-e${idx}-age-error`}>Indiquez un âge entre 18 et 99 ans.</p>}
      <p className="text-[11.5px] text-slate-500 mt-1.5">Utile pour estimer la durée possible du financement et l'assurance.</p>
      <SubLabel>Situation professionnelle</SubLabel>
      <div className="grid grid-cols-2 gap-2">
        {SITUATIONS.map((s) => (
          <button key={s.key} type="button" data-testid={`sim-e${idx}-situation-${s.key}`} aria-pressed={e.situation_pro === s.key}
            onClick={() => set({ situation_pro: s.key, periode_essai: null, titulaire: null, anciennete: null, autre_detail: null })}
            className={`min-h-[52px] px-2 rounded-xl border-2 font-bold text-[13px] leading-tight sim-chip ${e.situation_pro === s.key ? "border-[color:var(--cf-primary)] bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary-text)]" : "border-slate-200 bg-white text-slate-800"}`}>
            {s.label}
          </button>
        ))}
      </div>
      <SubQuestions e={e} set={set} testid={`sim-e${idx}`} />
      <div className="mt-8"><BigButton onClick={next} disabled={!ok} testid={`sim-profil_e${idx}-next`}>Suivant →</BigButton></div>
    </div>
  );
}

export function StepRevenus({ draft, patchNested, next, idx }) {
  const key = `emprunteur_${idx}`;
  const e = draft[key];
  const set = (p) => patchNested(key, p);
  const allowZero = e.situation_pro === "autre";
  const ok = (allowZero ? e.revenus_nets_mensuels !== "" : Number(e.revenus_nets_mensuels) > 0) && e.revenus_variables != null;
  return (
    <div>
      <StepTitle emoji="💼" title={idx === 1 ? "Vos revenus" : "Revenus du second emprunteur"} />
      <SubLabel>{REVENU_LABEL[e.situation_pro] || REVENU_LABEL.autre}</SubLabel>
      <AmountInput value={e.revenus_nets_mensuels} onChange={(x) => set({ revenus_nets_mensuels: x })} suffix="€/mois" placeholder="2500" testid={`sim-revenus_e${idx}-revenus-input`} autoFocus />
      <SubLabel>Une partie est variable (primes, commissions, 13e mois) ?</SubLabel>
      <YesNo value={e.revenus_variables} onChange={(v) => set({ revenus_variables: v })} testid={`sim-e${idx}-variable`} />
      {e.revenus_variables === true && (
        <>
          <SubLabel>Part variable mensuelle approximative <span className="text-slate-400 font-semibold">(facultatif)</span></SubLabel>
          <AmountInput small value={e.revenus_variables_montant} onChange={(x) => set({ revenus_variables_montant: x })} suffix="€/mois" testid={`sim-e${idx}-variable-montant`} />
        </>
      )}
      <div className="mt-8"><BigButton onClick={next} disabled={!ok} testid={`sim-revenus_e${idx}-next`}>Suivant →</BigButton></div>
    </div>
  );
}

export function StepAutresRevenus({ draft, patch, patchNested, next }) {
  return (
    <div>
      <StepTitle emoji="💵" title="Le foyer perçoit-il d'autres revenus ?" hint="Loyers perçus, pensions, autres revenus réguliers" />
      <YesNo value={draft.has_autres_revenus} testid="sim-autres" onChange={(v) => { patch({ has_autres_revenus: v }); if (v === false) setTimeout(next, 160); }} />
      {draft.has_autres_revenus === true && (
        <>
          <div className="space-y-3 mt-5">
            {[{ k: "locatifs", label: "Revenus locatifs (bruts)" }, { k: "pensions", label: "Pensions reçues" }, { k: "autres", label: "Autres revenus réguliers" }].map((f) => (
              <div key={f.k}>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">{f.label}</div>
                <AmountInput small value={draft.autres_revenus[f.k]} onChange={(x) => patchNested("autres_revenus", { [f.k]: x })} suffix="€/mois" testid={`sim-autres-${f.k}-input`} />
              </div>
            ))}
          </div>
          <div className="mt-8"><BigButton onClick={next} testid="sim-autres-next">Suivant →</BigButton></div>
        </>
      )}
    </div>
  );
}
